/**
 * useWalletVault - React hook for wallet vault management
 * Includes encryption lock/unlock state management
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import walletVault, { WALLET_CATEGORIES, WALLET_STATUS } from '../services/walletVault';
import { useNetwork } from '../WalletProvider';

export function useWalletVault() {
  const { rpcEndpoint } = useNetwork();
  const [wallets, setWallets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingBalances, setCheckingBalances] = useState(false);
  
  // Encryption state
  const [isLocked, setIsLocked] = useState(true);
  const [isEncryptionSetup, setIsEncryptionSetup] = useState(false);
  const [unlockError, setUnlockError] = useState(null);

  // Initialize vault and subscribe to state changes
  useEffect(() => {
    walletVault.init();
    
    // Subscribe to vault state changes
    const unsubscribe = walletVault.subscribe((state) => {
      setIsLocked(state.locked);
      if (!state.locked && state.loaded) {
        setWallets(walletVault.getAll());
        setGroups(walletVault.getGroups());
      }
    });
    
    // Set initial state
    setIsEncryptionSetup(walletVault.isEncryptionSetup());
    setIsLocked(walletVault.isLocked());
    
    if (!walletVault.isLocked()) {
      setWallets(walletVault.getAll());
      setGroups(walletVault.getGroups());
    }
    
    setLoading(false);
    
    return () => unsubscribe();
  }, []);

  // Refresh state from vault
  const refresh = useCallback(() => {
    setWallets(walletVault.getAll());
    setGroups(walletVault.getGroups());
  }, []);

  // Generate single wallet
  const generateWallet = useCallback((options = {}) => {
    const wallet = walletVault.generateWallet(options);
    refresh();
    return wallet;
  }, [refresh]);

  // Generate batch of wallets
  const generateBatch = useCallback((count, options = {}) => {
    const newWallets = walletVault.generateBatch(count, options);
    refresh();
    return newWallets;
  }, [refresh]);

  // Import wallet
  const importWallet = useCallback((privateKey, options = {}) => {
    const wallet = walletVault.importWallet(privateKey, options);
    refresh();
    return wallet;
  }, [refresh]);

  // Import batch
  const importBatch = useCallback((walletsData) => {
    const result = walletVault.importBatch(walletsData);
    refresh();
    return result;
  }, [refresh]);

  // Update wallet
  const updateWallet = useCallback((id, updates) => {
    const wallet = walletVault.updateWallet(id, updates);
    refresh();
    return wallet;
  }, [refresh]);

  // Delete wallet
  const deleteWallet = useCallback((id) => {
    const result = walletVault.deleteWallet(id);
    refresh();
    return result;
  }, [refresh]);

  // Delete batch
  const deleteBatch = useCallback((ids) => {
    const result = walletVault.deleteBatch(ids);
    refresh();
    return result;
  }, [refresh]);

  // Archive wallet
  const archiveWallet = useCallback((id) => {
    const result = walletVault.archiveWallet(id);
    refresh();
    return result;
  }, [refresh]);

  // Create group
  const createGroup = useCallback((name, options = {}) => {
    const group = walletVault.createGroup(name, options);
    refresh();
    return group;
  }, [refresh]);

  // Delete group
  const deleteGroup = useCallback((groupId, deleteWallets = false) => {
    const result = walletVault.deleteGroup(groupId, deleteWallets);
    refresh();
    return result;
  }, [refresh]);

  // Check balances
  const checkBalances = useCallback(async (walletIds = null) => {
    setCheckingBalances(true);
    try {
      const ids = walletIds || wallets.map(w => w.id);
      const results = await walletVault.checkBalances(ids, rpcEndpoint);
      refresh();
      return results;
    } finally {
      setCheckingBalances(false);
    }
  }, [wallets, rpcEndpoint, refresh]);

  // Randomize buy amounts
  const randomizeBuyAmounts = useCallback((walletIds, range) => {
    const result = walletVault.randomizeBuyAmounts(walletIds, range);
    refresh();
    return result;
  }, [refresh]);

  // Export wallets
  const exportWallets = useCallback((walletIds = null) => {
    return walletVault.exportWallets(walletIds);
  }, []);

  // Download export
  const downloadExport = useCallback((walletIds = null, filename = null) => {
    walletVault.downloadExport(walletIds, filename);
  }, []);

  // Clear all
  const clearAll = useCallback(() => {
    walletVault.clearAll();
    refresh();
  }, [refresh]);

  // Setup encryption
  const setupEncryption = useCallback(async (password) => {
    try {
      setUnlockError(null);
      await walletVault.setupEncryption(password);
      setIsEncryptionSetup(true);
      setIsLocked(false);
      refresh();
      return { success: true };
    } catch (error) {
      setUnlockError(error.message);
      return { success: false, error: error.message };
    }
  }, [refresh]);

  // Unlock vault
  const unlock = useCallback(async (password) => {
    try {
      setUnlockError(null);
      await walletVault.unlock(password);
      setIsLocked(false);
      refresh();
      return { success: true };
    } catch (error) {
      setUnlockError(error.message);
      return { success: false, error: error.message };
    }
  }, [refresh]);

  // Lock vault
  const lock = useCallback(() => {
    walletVault.lock();
    setIsLocked(true);
    setWallets([]);
    setGroups([]);
  }, []);

  // Change password
  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      setUnlockError(null);
      await walletVault.changePassword(oldPassword, newPassword);
      return { success: true };
    } catch (error) {
      setUnlockError(error.message);
      return { success: false, error: error.message };
    }
  }, []);

  // Remove encryption
  const removeEncryption = useCallback(async (password) => {
    try {
      setUnlockError(null);
      await walletVault.removeEncryption(password);
      setIsEncryptionSetup(false);
      setIsLocked(false);
      refresh();
      return { success: true };
    } catch (error) {
      setUnlockError(error.message);
      return { success: false, error: error.message };
    }
  }, [refresh]);

  // Get by category
  const getByCategory = useCallback((category) => {
    return walletVault.getByCategory(category);
  }, []);

  // Get by group
  const getByGroup = useCallback((groupId) => {
    return walletVault.getByGroup(groupId);
  }, []);

  // Get by status
  const getByStatus = useCallback((status) => {
    return walletVault.getByStatus(status);
  }, []);

  // Get aged wallets
  const getAgedWallets = useCallback((minAgeDays = 7) => {
    return walletVault.getAgedWallets(minAgeDays);
  }, []);

  // Get keypair for signing
  const getKeypair = useCallback((id) => {
    return walletVault.getKeypair(id);
  }, []);

  // Stats
  const stats = useMemo(() => walletVault.getStats(), [wallets]);

  // Bundle wallets (convenience)
  const bundleWallets = useMemo(() =>
    wallets.filter(w => w.category === WALLET_CATEGORIES.BUNDLE && w.status !== WALLET_STATUS.ARCHIVED),
    [wallets]
  );

  // Aged wallets (convenience)
  const agedWallets = useMemo(() =>
    wallets.filter(w => w.category === WALLET_CATEGORIES.AGED && w.status !== WALLET_STATUS.ARCHIVED),
    [wallets]
  );

  // Total balance
  const totalBalance = useMemo(() =>
    wallets.reduce((sum, w) => sum + (w.balance || 0), 0),
    [wallets]
  );

  // Total buy amount
  const totalBuyAmount = useMemo(() =>
    bundleWallets.reduce((sum, w) => sum + (w.buyAmount || 0), 0),
    [bundleWallets]
  );

  return {
    // State
    wallets,
    groups,
    loading,
    checkingBalances,
    stats,
    bundleWallets,
    agedWallets,
    totalBalance,
    totalBuyAmount,
    
    // Encryption state
    isLocked,
    isEncryptionSetup,
    unlockError,
    
    // Actions
    refresh,
    generateWallet,
    generateBatch,
    importWallet,
    importBatch,
    updateWallet,
    deleteWallet,
    deleteBatch,
    archiveWallet,
    createGroup,
    deleteGroup,
    checkBalances,
    randomizeBuyAmounts,
    exportWallets,
    downloadExport,
    clearAll,
    
    // Encryption actions
    setupEncryption,
    unlock,
    lock,
    changePassword,
    removeEncryption,
    
    // Getters
    getByCategory,
    getByGroup,
    getByStatus,
    getAgedWallets,
    getKeypair,
    
    // Constants
    WALLET_CATEGORIES,
    WALLET_STATUS
  };
}

export { WALLET_CATEGORIES, WALLET_STATUS };
export default useWalletVault;
